
import Vue from 'vue';
import { BoardsGroupModel, ClassroomModel, CourseModel, UserModel } from '@descomplica/dex-models';
import PostGradAssessmentsCard from '../components/cards/assessments/PostGradAssessmentsCard.vue';
import DisciplineCard from '../components/cards/DisciplineCard.vue';
import CourseHeader from '../components/headers/CourseHeader.vue';
import PeriodModal from '../components/modals/PeriodModal.vue';
import Sort from '@/helpers/sort';
import { MicrocertificateModel } from '@/models/microcertificates/microcertificate.model';
import {
  ClassroomsGroup,
  filterClassroomsByPeriod,
  groupClassroomsByType,
  groupPostgradClassroomsInModules,
} from '@/helpers/classrooms';
import { getCourseCategory } from '@/helpers/category';
import { Period } from '@/store/modules/periods/state';
import StudyPlanModal from '../components/modals/studyPlan/undergrad/StudyPlanModal.vue';
import Analytics from '@/helpers/analytics';
import { EVENT_LABELS } from '@/helpers/analytics/labels';
import SoonModal from '../components/modals/boards/SoonModal.vue';

interface Data {
  showPeriodMenu: boolean;
  showStudyPlan: boolean;
  showSoonModal: boolean;
  classroomsGroups: ClassroomsGroup[];
}

export default Vue.extend({
  components: {
    CourseHeader,
    DisciplineCard,
    PeriodModal,
    PostGradAssessmentsCard,
    StudyPlanModal,
    SoonModal,
  },

  data(): Data {
    return {
      showPeriodMenu: false,
      showStudyPlan: false,
      showSoonModal: false,
      classroomsGroups: [],
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    isLoadingUserBaseProgress(): boolean {
      return this.$store.getters.isLoadingUserBaseProgress;
    },
    course(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    microcertificates(): MicrocertificateModel[] {
      return this.$store.getters.userMicrocertificates;
    },
    isPreparingDownloadMicroCertificate(): boolean {
      return this.$store.getters.isGeneratingMicroCertificate;
    },
    allUserClassrooms(): ClassroomModel[] {
      const shouldAddClassroomsWithCourseIdNull = true;
      return this.user
        .getClassroomsByCourseId(this.course.id, shouldAddClassroomsWithCourseIdNull)
        .sort(Sort.byName);
    },
    boardsGroups(): BoardsGroupModel[] {
      return this.$store.getters.getBoardsGroupsByCourseId(this.course.id);
    },
    isPosCategory(): boolean {
      return getCourseCategory(this.course) === 'pos';
    },
    userCurrentPeriod(): Period {
      return this.$store.getters.userCurrentPeriod;
    },
    userCurrentClassroomsByPeriod(): ClassroomModel[] {
      return this.$store.getters.currentClassroomsByPeriod;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    showPostgradAssessmentsCard(): boolean {
      return this.$store.getters.showPostgradAssessmentsCard;
    },
    canViewUndergradStudyPlan(): boolean {
      return this.$store.getters.canViewUndergradStudyPlan;
    },
    studyPlanClassroomsByEnrollmentDate(): number[] {
      return this.$store.getters.studyPlanClassroomsByEnrollmentDate;
    },
  },
  watch: {
    async userCurrentPeriod(newPeriod: Period) {
      await this.$store.commit('SET_CURRENT_CLASSROOMS_BY_PERIOD', newPeriod.number);

      this.getUndergradCurrentPeriod();
    },
  },
  async mounted() {
    await this.$store.commit('SET_LOADING_PERIODS', true);
    if (!this.isPosCategory) {
      await this.$store.dispatch('PULL_USER_PERIODS', {
        course: this.course.name,
        classroomsIds: this.allUserClassrooms.map((classroom: ClassroomModel) => classroom.id),
      });
    } else {
      await this.$store.commit('SET_LOADING_PERIODS', false);
    }
    await this.setClassroomsGroups();
  },
  methods: {
    async setClassroomsGroups(): Promise<void> {
      if (this.isPosCategory) {
        await this.$store.dispatch('GET_BOARDS_GROUPS_FROM_COURSE', { courseId: this.course.id });
        this.classroomsGroups = this.boardsGroups.length
          ? groupPostgradClassroomsInModules(this.allUserClassrooms, this.boardsGroups)
          : groupClassroomsByType(this.allUserClassrooms);

        return;
      }

      this.getUndergradCurrentPeriod();
    },
    togglePeriodMenu(): void {
      this.showPeriodMenu = !this.showPeriodMenu;
    },
    getUndergradCurrentPeriod(): void {
      const filteredClassroomsByPeriod: (ClassroomModel | undefined)[] = filterClassroomsByPeriod(
        this.allUserClassrooms,
        this.userCurrentClassroomsByPeriod,
      );

      this.classroomsGroups = groupClassroomsByType(filteredClassroomsByPeriod as ClassroomModel[]);
    },
    openStudyPlan(): void {
      this.showStudyPlan = true;
      this.trackingStudyPlanInteracted();
    },
    trackingStudyPlanInteracted(): void {
      Analytics.trackEvent(EVENT_LABELS.studyPlanInteracted, {
        interaction_type: 'Study Plan Opened',
      });
    },
    openSoonModal(): void {
      this.showSoonModal = true;
      this.trackingSoonModalInteracted();
    },
    trackingSoonModalInteracted(): void {
      Analytics.trackEvent(EVENT_LABELS.modalViewed, {
        interaction_type: 'Soon Modal Opened',
      });
    },
  },
});
